import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * WorkRefreshContext - Global state for triggering data refresh across components
 * Solves the "My Work doesn't refresh" bug
 */
const WorkRefreshContext = createContext();

export const WorkRefreshProvider = ({ children }) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [lastAction, setLastAction] = useState(null);

  // Trigger a refresh across all listening components
  const triggerRefresh = useCallback((action = 'unknown') => {
    setRefreshTrigger(prev => prev + 1);
    setLastAction(action);
  }, []);

  // Specific refresh triggers
  const refreshAfterEvaluation = useCallback(() => {
    triggerRefresh('evaluation_created');
  }, [triggerRefresh]);

  const refreshAfterSessionSave = useCallback(() => {
    triggerRefresh('session_saved');
  }, [triggerRefresh]);

  const refreshAfterSessionCreate = useCallback(() => {
    triggerRefresh('session_created');
  }, [triggerRefresh]);

  const value = {
    refreshTrigger,
    lastAction,
    triggerRefresh,
    refreshAfterEvaluation,
    refreshAfterSessionSave,
    refreshAfterSessionCreate
  };

  return (
    <WorkRefreshContext.Provider value={value}>
      {children}
    </WorkRefreshContext.Provider>
  );
};

export const useWorkRefresh = () => {
  const context = useContext(WorkRefreshContext);
  if (!context) {
    throw new Error('useWorkRefresh must be used within WorkRefreshProvider');
  }
  return context;
};

export default WorkRefreshContext;
